import React, { useState, useEffect, useCallback } from 'react';
import { Button, message, Form, Modal } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import AccountTable from './AccountTable';
import AccountModal from './AccountModal';
import SearchBar from './common/SearchBar';
import { useDebounce } from 'use-debounce';

const AccountList = ({ baseAccountId }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [form] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  useEffect(() => {
    fetchAccounts();
  }, [baseAccountId]);

  useEffect(() => {
    fetchAccounts(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  const fetchAccounts = async (query = '') => {
    setSearchLoading(true);
    try {
      const params = { BaseAccountId: baseAccountId };
      if (query) params.AccountName = query;
      const response = await axios.get(`${API_BASE_URL}Account/List`, { params });
      if (response.data.code === 0) {
        setAccounts(response.data.data || []);
      } else {
        message.error(response.data.message || 'فشل في جلب الحسابات');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'خطأ في جلب الحسابات';
      message.error(errorMessage);
    }
    setSearchLoading(false);
    setLoading(false);
  };

  const handleCreate = () => {
    setEditingAccount(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingAccount(record);
    form.setFieldsValue({
      Name: record.name,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'تأكيد الحذف',
      content: 'هل أنت متأكد من حذف هذا الحساب؟',
      okText: 'نعم',
      cancelText: 'لا',
      onOk: async () => {
        try {
          const response = await axios.delete(`${API_BASE_URL}Account/Delete/${id}`);
          if (response.data.code === 0) {
            message.success(response.data.message || 'تم حذف الحساب بنجاح');
            fetchAccounts();
          } else {
            message.error(response.data.message || 'فشل في حذف الحساب');
          }
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'خطأ في حذف الحساب';
          message.error(errorMessage);
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      values.BaseAccountId = baseAccountId;
      if (editingAccount && editingAccount.id) {
        const response = await axios.put(`${API_BASE_URL}Account/Update/${editingAccount.id}`, values);
        if (response.data.code === 0) {
          message.success('تم تحديث الحساب بنجاح');
          setIsModalVisible(false);
          fetchAccounts();
        } else {
          message.error(response.data.message || 'فشل في تحديث الحساب');
        }
      } else {
        const response = await axios.post(`${API_BASE_URL}Account/Create`, values);

        if (response.data.code === 0) {
          message.success('تم إنشاء الحساب بنجاح');
          setIsModalVisible(false);
          fetchAccounts();
        } else {
          message.error(response.data.message || 'فشل في إنشاء الحساب');
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'خطأ في حفظ الحساب';
      message.error(errorMessage);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          إضافة حساب
        </Button>
        <SearchBar
          onSearch={setSearchTerm}
          loading={searchLoading}
          placeholder="ابحث بالاسم..."
        />
      </div>
      <AccountTable
        accounts={accounts}
        loading={loading || searchLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        locale={{ emptyText: searchTerm ? 'لا توجد حسابات بهذا الاسم' : 'لا توجد حسابات' }}
      />
      <AccountModal
        isVisible={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        editingAccount={editingAccount}
        form={form}
      />
    </div>
  );
};

export default AccountList;
